/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnDestroy, type OnInit, Directive } from '@angular/core';
import { Router } from '@angular/router';
import { EventService, PermissionService, TimeService, UtilService } from '@dm/core';
import { GlobalDataService } from 'app/common/services';
import { type Alarm, type AlarmCountAndRecent } from 'app/common/types/mo';
import { SWITCH } from 'app/config';
import { type Subscription } from 'rxjs';

import { HomeStrService } from '../home-str.service';
import { DEFAULT_QUANTITY } from './alarm-chart.const';

export enum AlarmLevelE {
    Warning = 3,
    Major = 5,
    Critical = 6
}

@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export abstract class AlarmAbstract implements OnInit, OnDestroy {
    i18n = this.str.alarm;
    i18nKey = this.str.getKey;
    AlarmLevelE = AlarmLevelE;

    data: AlarmCountAndRecent = {
        critical: 0,
        major: 0,
        warning: 0,
        total: 0,
        recent: [],
    };

    // 上一次新告警提示时记录的告警数
    lastAlarmCount: number;
    // 新告警提示信息
    newAlarmTips: string;

    levelIcon = {
        [AlarmLevelE.Warning]: '#app-icon-alarms-minor-new',
        [AlarmLevelE.Major]: '#icon-alarms-major',
        [AlarmLevelE.Critical]: '#icon-alarms-critical',
    };

    checkShow = this.permissionService.checkShow;

    protected subs$: Subscription[] = [];

    constructor(
        protected str: HomeStrService,
        protected eventService: EventService,
        protected timeService: TimeService,
        public utilService: UtilService,
        protected router: Router,
        protected globalDataService: GlobalDataService,
        protected permissionService: PermissionService,
    ) { }

    ngOnInit(): void {
        this.initData();
    }

    ngOnDestroy(): void {
        for (const sub$ of this.subs$) {
            sub$.unsubscribe();
        }
    }

    initData(quantity = DEFAULT_QUANTITY): void {
        this.subs$.push(
            this.eventService.subscribe('alarmUpdate', (data) => {
                this.updateData(data, quantity);
            }),
        );
        this.updateData(this.globalDataService.getAlarmData(), quantity);
    }

    /**
     * 更新数据
     * @param param0 各类告警
     * @param quantity 展示告警数量
     */
    updateData({ critical, major, warning, recent, total, isFetched }: AlarmCountAndRecent, quantity = DEFAULT_QUANTITY): void {
        if (isFetched) {
            if (this.lastAlarmCount === undefined) {
                this.lastAlarmCount = critical + major + warning;
            } else if (critical + major + warning > this.lastAlarmCount) {
                this.newAlarmTips = this.str.getNewAlarmTips(critical + major + warning - this.lastAlarmCount);
            } else {
                this.newAlarmTips = '';
                // 更新上一次告警数
                this.lastAlarmCount = critical + major + warning;
            }
        }
        this.data.critical = critical;
        this.data.major = major;
        this.data.warning = warning;
        this.data.total = total;
        this.data.recent = recent.slice(0, quantity);
    }

    renderTime = (time: number): string => this.timeService.formatUTC(time * 1e3);

    /**
     * 关闭新告警提示，更新上一次告警数
     */
    closeNewAlarmTips(): void {
        this.newAlarmTips = '';
        this.lastAlarmCount = this.data.critical + this.data.major + this.data.warning;
    }

    /**
     * 跳转到告警详情
     * @param alarm
     */
    jumpToAlarmDetail(alarm?: Alarm): void {
        const url = SWITCH.IS_SIMPLE_MODE
            ? '/simple/alarms-and-events'
            : '/monitor/alarms-and-events';

        this.router.navigate([url], {
            queryParams: alarm ? {
                sequence: alarm.sequence,
            } : null,
        });
    }

    /**
     * 跳转到告警详情，包含state参数
     * @param alarm
     */
    jumpToAlarmDetailWithState(alarm?: Alarm): void {
        const url = SWITCH.IS_SIMPLE_MODE
            ? '/simple/alarms-and-events'
            : '/monitor/alarms-and-events';

        this.router.navigate([url], {
            queryParams: alarm ? {
                sequence: alarm.sequence,
            } : null,
            state: alarm,
        });
    }

    /**
     * 跳转到告警页面
     * @param level 告警级别
     */
    more(level?: number): void {
        const url = SWITCH.IS_SIMPLE_MODE
            ? '/simple/alarms-and-events'
            : '/monitor/alarms-and-events';

        this.router.navigate([url], {
            queryParams: {
                level,
            },
        });
    }

    /**
     * 跳转到异常检测
     */
    goToPerformanceAnomaly(): void {
        this.router.navigate(['/monitor/performance/performance-anomaly']);
    }
}
